import type { ComponentType } from 'react';
import { Navigate, createBrowserRouter, useParams } from 'react-router-dom';
import { App } from './App';
import { Hub } from '../shell/Hub';
import { ModulePage } from '../shell/ModulePage';
import { AdminPanel } from '../shell/admin/AdminPanel';
import { usePlatform } from '../data/queries/usePlatform';
import { EVENTS_MODULE_VIEWS, type SystemModuleViewProps } from '../systems/events/registry';
import { RequireAuth } from '../auth/RequireAuth';
import { Login } from '../auth/Login';
import { AuthCallback } from '../auth/AuthCallback';
import { SilentCallback } from '../auth/SilentCallback';

/**
 * Per-system module → view-component registries. A system not listed
 * here (or a module not listed inside its sub-registry) falls back to
 * the generic `<ModulePage/>` placeholder.
 */
const SYSTEM_MODULE_VIEWS: Record<string, Record<string, ComponentType<SystemModuleViewProps>>> = {
  events: EVENTS_MODULE_VIEWS,
};

function SystemRedirect() {
  const { systemId } = useParams<{ systemId: string }>();
  const { data } = usePlatform();
  if (!data || !systemId) return null;
  const system = data.suites.find((s) => s.id === systemId);
  if (!system) return <Navigate to="/" replace />;
  const first = system.modules[0];
  if (!first) {
    // System has no modules — render empty ModulePage stand-in.
    return null;
  }
  return <Navigate to={`/system/${systemId}/${first.id}`} replace />;
}

function SystemModuleRoute() {
  const { systemId, moduleId } = useParams<{ systemId: string; moduleId: string }>();
  const { data } = usePlatform();
  if (!data || !systemId || !moduleId) return null;
  const system = data.suites.find((s) => s.id === systemId);
  if (!system) return <Navigate to="/" replace />;
  const module = system.modules.find((m) => m.id === moduleId);
  if (!module) return <Navigate to={`/system/${systemId}`} replace />;

  // Look up a specialised view component (e.g. funnel → FunnelView).
  // Fall through to the generic placeholder for everything else.
  const ModuleComponent =
    SYSTEM_MODULE_VIEWS[systemId]?.[moduleId] ?? ModulePage;
  return <ModuleComponent system={system} module={module} />;
}

export const router = createBrowserRouter([
  // Public routes — must NOT be wrapped in RequireAuth/App shell.
  { path: '/login',                element: <Login /> },
  { path: '/auth/callback',        element: <AuthCallback /> },
  { path: '/auth/silent-callback', element: <SilentCallback /> },

  // Protected app shell.
  {
    path: '/',
    element: (
      <RequireAuth>
        <App />
      </RequireAuth>
    ),
    children: [
      { index: true,                        element: <Hub /> },
      { path: 'admin',                      element: <AdminPanel /> },
      { path: 'system/:systemId',           element: <SystemRedirect /> },
      { path: 'system/:systemId/:moduleId', element: <SystemModuleRoute /> },
      { path: '*',                          element: <Navigate to="/" replace /> },
    ],
  },
]);
